import { Component, signal, computed, inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { tenant } from '../../../tenants/tenant';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { AuthService } from '../../core/services/auth-service';
import { Router } from '@angular/router';
import { SettingsService } from '../../core/services/settings-service';
import { UserService } from '../../core/services/user-service';
import { AppSettings } from '../../core/models/app-settings.model';
import { isAdminGroup } from '../../core/models/user.model';
import { ChatFeedbackService } from '../../core/services/chat-feedback-service';
import { combineLatest, filter, Subscription, take } from 'rxjs';
import { DynamicDialog } from '../dynamic-dialog/dynamic-dialog';

export interface NavigationItem {
  label: string;
  route?: string;
  badge?: number;
  group?: string;
  featureName?: string;
  requiresAdmin?: boolean;
}

interface NavigationGroup {
  groupKey: string;
  items: NavigationItem[];
}

@Component({
  selector: 'nexa-navbar',
  imports: [DrawerModule, ButtonModule, AvatarModule, BadgeModule, TranslatePipe, DynamicDialog],
  templateUrl: './navbar.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class Navbar implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly settingsService = inject(SettingsService);
  private readonly userService = inject(UserService);
  private readonly translate = inject(TranslateService);
  private readonly chatFeedback = inject(ChatFeedbackService);

  private dataSubscription: Subscription | undefined;

  // App info
  appVersion = signal(environment.appVersion);
  appYear = signal(environment.appYear);
  appName = signal(environment.appName);
  showTestBanner = signal((environment.envName as string) !== 'prod');

  // Drawer
  drawerVisible = signal(false);

  // Branding
  showSupplierLogo = signal(tenant.showSupplierLogo ?? true);
  brandName = signal(tenant.brandName);
  brandLogoImage = signal(tenant.brandLogoImage);

  // User data
  userName = computed( () => {
    const user = this.authService.user();
    if ( !user ) return '';
    const name = user.name || '';
    const surname = user.surname || '';
    if ( user.userSaml ) {
      return `${name}`.trim();
    } else {
      return `${name} ${surname}`.trim();
    }
  } );
  userRole = computed(() => {
    const user = this.authService.user();
    const group = user?.group?.toLowerCase() || '';
    switch (group) {
      case 'sys_admin':
        return 'System Admin';
      case 'admin':
        return 'Admin';
      case 'user':
        return 'Utente';
      default:
        return user?.group || '';
    }
  });
  userAvatar = signal('');

  showLogoutDialog = signal(false);

  feedbackAvailable = this.chatFeedback.available;

  logoutMessage = computed(() =>
    this.translate.instant('NAVBAR.LOGOUT_CONFIRM_MESSAGE', { tenantName: this.brandName() })
  );

  logoutDialogConfig = computed(() => ({
    header: this.userName() || this.translate.instant('NAVBAR.LOGOUT'),
    showHeader: true,
    width: '25rem',
    modal: true,
    closable: true,
    dismissableMask: true,
    customClass: 'logout-dialog hide-close-button',
    position: 'topright' as const,
    footerButtons: [
      {
        label: this.translate.instant('COMMON.CANCEL'),
        severity: 'secondary' as const,
        outlined: true,
        action: () => this.closeLogoutDialog(),
        style: { 'min-width': '6rem' }
      },
      {
        label: this.translate.instant('NAVBAR.LOGOUT'),
        severity: 'danger' as const,
        action: () => this.confirmLogout(),
        style: { 'min-width': '6rem' }
      }
    ]
  }));

  navigationItems = signal<NavigationItem[]>([...tenant.navigationItems]);

  groupedNavigationItems = computed<NavigationGroup[]>(() => {
    const items = this.navigationItems();
    const groupsMap = new Map<string, NavigationItem[]>();

    items.forEach((item) => {
      const groupKey = item.group || 'default';
      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, []);
      }
      groupsMap.get(groupKey)!.push(item);
    });

    return Array.from(groupsMap.entries()).map(([groupKey, groupItems]) => ({
      groupKey,
      items: groupItems,
    }));
  });

  async ngOnInit(): Promise<void> {
    if (!this.authService.user()) {
      try {
        await this.authService.getCurrentUser();
      } catch (error) {
        console.error('Failed to load user data:', error);
      }
    }

    this.settingsService.appSettings$.pipe(take(1)).subscribe(currentSettings => {
      if (!currentSettings) {
        this.settingsService.loadAllSettings().subscribe();
      }
    });

    const settings$ = this.settingsService.appSettings$;
    const user$ = this.userService.getUser();

    this.dataSubscription = combineLatest([settings$, user$]).pipe(
      filter(([settings, user]) => !!settings && !!user)
    ).subscribe(([settings, user]) => {
      this.updateMenuVisibility(settings!, user!);
      this.authService.mergeUserProfile(user!);
    });
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }

  private updateMenuVisibility(settings: AppSettings[], user: { group?: string | null }): void {
    const isUserAdmin = isAdminGroup(user.group);

    const allItems = [...tenant.navigationItems];

    const visibleItems = allItems.filter(item => {
      if (!item.featureName) {
        return true;
      }

      const featureSetting = settings.find(
        s => s.featureName?.toLowerCase() === item.featureName?.toLowerCase()
      );
      const isFeatureEnabled = featureSetting?.isEnabled ?? false;

      if (!isFeatureEnabled) {
        return false;
      }

      if (item.requiresAdmin && !isUserAdmin) {
        return false;
      }

      return true;
    });

    this.navigationItems.set(visibleItems);
  }

  toggleDrawer(): void {
    this.drawerVisible.set(!this.drawerVisible());
  }

  showLogoutConfirmation(): void {
    this.showLogoutDialog.set(true);
  }

  closeLogoutDialog(): void {
    this.showLogoutDialog.set(false);
  }

  requestFeedback(): void {
    this.chatFeedback.request();
  }

  async confirmLogout(): Promise<void> {
    this.showLogoutDialog.set(false);
    const loginFallback = tenant.samlEnabled ? '/login-admin' : '/login';
    try {
      await this.authService.logout();
      if (!tenant.samlEnabled) {
        await this.router.navigate(['/login']);
      }
    } catch (error) {
      console.error('Logout error:', error);
      await this.router.navigate([loginFallback]);
    }
  }

  navigateTo(route?: string): void {
    if (route) {
      this.drawerVisible.set(false);
      this.router.navigate([route]);
    }
  }
}
